import { Environment } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import {
  isDockerEnvironment,
  isKubernetesEnvironment,
} from '@/react/portainer/environments/utils';

import { PolicyType, PolicyEnvironmentType } from '../types';

import {
  getPolicyTypeMetadata,
  getAllPolicyTypesMetadata,
  PolicyTypeMetadata,
} from './policy-types-metadata';

/**
 * Get the supported environment types for a policy type
 */
export function getSupportedEnvironmentTypes(
  policyType: PolicyType
): PolicyEnvironmentType[] {
  const metadata = getPolicyTypeMetadata(policyType);
  return metadata.supportedEnvironmentTypes;
}

/**
 * Check if an environment is compatible with a policy type
 */
function isEnvironmentCompatible(
  envType: number,
  policyType: PolicyType
): boolean {
  const supportedTypes = getSupportedEnvironmentTypes(policyType);

  // Check if this is a Kubernetes environment
  if (isKubernetesEnvironment(envType)) {
    return supportedTypes.includes('kubernetes');
  }

  // Check if this is a Docker-based environment (Docker, Podman, or Swarm)
  if (isDockerEnvironment(envType)) {
    return (
      supportedTypes.includes('docker') ||
      supportedTypes.includes('podman') ||
      supportedTypes.includes('swarm')
    );
  }

  return false;
}

/**
 * Get the count of environment groups that are compatible with a policy type
 * A group is compatible if it has at least one environment of a supported type
 */
export function getCompatibleGroupsCount(
  policyType: PolicyType,
  environments: Environment[],
  groups: EnvironmentGroup[]
): number {
  return groups.filter((group) => {
    // Filter out the "Unassigned" group
    if (group.Id === 1) {
      return false;
    }

    // Find all environments in this group
    const groupEnvironments = environments.filter(
      (env) => env.GroupId === group.Id
    );

    // Check if any environment in the group is compatible with this policy type
    return groupEnvironments.some((env) =>
      isEnvironmentCompatible(env.Type, policyType)
    );
  }).length;
}

/**
 * Get only policy types that have at least one compatible environment group
 * This is used to filter out (hide) policy types with 0 compatible groups
 */
export function getVisiblePolicyTypes(
  environments: Environment[],
  groups: EnvironmentGroup[]
): PolicyTypeMetadata[] {
  const allPolicyTypes = getAllPolicyTypesMetadata();

  return allPolicyTypes.filter((metadata) => {
    const compatibleCount = getCompatibleGroupsCount(
      metadata.id,
      environments,
      groups
    );
    return compatibleCount > 0;
  });
}

/**
 * Get the environment type from a policy type for backwards compatibility
 */
export function getEnvironmentTypeFromPolicyType(
  policyType: PolicyType
): PolicyEnvironmentType {
  // Kubernetes policies
  if (policyType.endsWith('-k8s')) {
    return 'kubernetes';
  }

  // Docker policies (also supports Podman and Swarm)
  return 'docker';
}
